import * as React from 'react'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import { getCubeAPI } from '../../../../../Cube'
import { QueryRenderer } from '@cubejs-client/react'
import { Skeleton } from 'antd'
import { parseAndDownloadCSV, GrPrice } from '../../../../../utilities/helpers'
import { ReportFilter } from '../ReportFilter'
import { ReportFilterStore } from '../ReportFilterStore'
import Helmet from 'react-helmet'
import { Track } from '../../../../../Track'
import { ICubeQuery } from '@getgreenline/shared'
import { ResultSet } from '@cubejs-client/core'
import { Button, Popover, Tooltip } from 'antd-v4'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Link } from 'react-router'
import { Routes } from '../../../../../constants/Routes'
import { COLORS } from '../../../../../constants/Colors'

interface Props {
  companyId: number
  reportFilterStore?: ReportFilterStore
}

interface State {
  fullPaymentsQuery?: ICubeQuery
  fullTransactionsQuery?: ICubeQuery
  fullDiscountQuery?: ICubeQuery
  fullRefundsQuery?: ICubeQuery
}

type RendererProps = { reportData: CubeRawDataItem[] }

interface CubeRawDataItem {
  [x: string]: number
}

enum ColumeTitles {
  ACTIVATIONS = 'Activations',
  REDEMPTIONS = 'Redemptions',
  DISCOUNTS = 'Discounts',
  REFUNDS = 'Refunds',
}

enum QueryMeasures {
  ACTIVATIONS_COUNT = 'PaymentLines.countGiftCardActivations',
  ACTIVATIONS_AMOUNT = 'PaymentLines.giftCardGross',
  REDEMPTIONS_COUNT = 'Transactions.countGiftCardRedemptions',
  REDEMPTIONS_AMOUNT = 'Transactions.giftCardRedemptions',
  DISCOUNTS_COUNT = 'PaymentLines.countGiftCardDiscounts',
  DISCOUNTS_AMOUNT = 'PaymentLines.giftCardDiscounts',
  REFUNDS_COUNT = 'PaymentLines.countGiftCardRefunds',
  REFUNDS_AMOUNT = 'PaymentLines.giftCardRefunds',
}

enum CSVHeaders {
  ACTIVATIONS_COUNT = 'Activations Count',
  ACTIVATIONS_AMOUNT = 'Activations Amount',
  REDEMPTIONS_COUNT = 'Redemptions Count',
  REDEMPTIONS_AMOUNT = 'Redemptions Amount',
  DISCOUNTS_COUNT = 'Discounts Count',
  DISCOUNTS_AMOUNT = 'Discounts Amount',
  REFUNDS_COUNT = 'Refunds Count',
  REFUNDS_AMOUNT = 'Refunds Amount',
}

const limit = 10000 // change this to state when needed

@inject('reportFilterStore')
@observer
export class GiftCardReport extends React.Component<Props, State> {
  // Static property that stores loaded report data
  data: any

  constructor(props: Props) {
    super(props)

    this.state = {
      fullPaymentsQuery: undefined,
      fullTransactionsQuery: undefined,
    }
  }

  componentDidMount = () => {
    const fullPaymentsQuery = this.getFullQuery(this.activationMeasures, 'Payments')
    const fullTransactionsQuery = this.getFullQuery(this.redemptionMeasures, 'Payments')
    const fullDiscountQuery = this.getFullQuery(this.discountMeasures, 'Payments')
    const fullRefundsQuery = this.getFullQuery(this.refundsMeasures, 'Payments')

    this.setState({ fullPaymentsQuery, fullTransactionsQuery, fullDiscountQuery, fullRefundsQuery })
  }

  get activationMeasures() {
    return [QueryMeasures.ACTIVATIONS_COUNT, QueryMeasures.ACTIVATIONS_AMOUNT]
  }

  get redemptionMeasures() {
    return [QueryMeasures.REDEMPTIONS_COUNT, QueryMeasures.REDEMPTIONS_AMOUNT]
  }

  get discountMeasures() {
    return [QueryMeasures.DISCOUNTS_COUNT, QueryMeasures.DISCOUNTS_AMOUNT]
  }

  get refundsMeasures() {
    return [QueryMeasures.REFUNDS_COUNT, QueryMeasures.REFUNDS_AMOUNT]
  }

  tableRenderer = ({ reportData }: RendererProps) => {
    if (reportData.length > 0) {
      return (
        <>
          {reportData.map((activationData, index) => {
            return (
              <tr key={`${ColumeTitles.ACTIVATIONS}_${index}`}>
                <td>{ColumeTitles.ACTIVATIONS}</td>
                <td className='text-right'>{activationData[QueryMeasures.ACTIVATIONS_COUNT]}</td>
                <td className='text-right'>
                  {GrPrice.convertDollarToDollar(activationData[QueryMeasures.ACTIVATIONS_AMOUNT])}
                </td>
              </tr>
            )
          })}
        </>
      )
    } else {
      return <div>No data</div>
    }
  }

  rowRenderer = ({ reportData }: RendererProps) => {
    if (reportData.length > 0) {
      return (
        <>
          {reportData.map((redemptionData, index) => {
            return (
              <tr key={`${ColumeTitles.REDEMPTIONS}_${index}`}>
                <td>{ColumeTitles.REDEMPTIONS}</td>
                <td className='text-right'>{redemptionData[QueryMeasures.REDEMPTIONS_COUNT]}</td>
                <td className='text-right'>
                  {GrPrice.convertDollarToDollar(redemptionData[QueryMeasures.REDEMPTIONS_AMOUNT])}
                </td>
              </tr>
            )
          })}
        </>
      )
    } else {
      return <div>No data</div>
    }
  }

  discountRenderer = ({ reportData }: RendererProps): JSX.Element => {
    if (reportData.length > 0) {
      return (
        <>
          {reportData.map((discountData, index) => {
            return (
              <tr key={`${ColumeTitles.DISCOUNTS}_${index}`}>
                <td>{ColumeTitles.DISCOUNTS}</td>
                <td className='text-right'>{discountData[QueryMeasures.DISCOUNTS_COUNT]}</td>
                <td className='text-right'>
                  {GrPrice.convertDollarToDollar(discountData[QueryMeasures.DISCOUNTS_AMOUNT])}
                </td>
              </tr>
            )
          })}
        </>
      )
    } else {
      return <div>No data</div>
    }
  }

  refundsRenderer = ({ reportData }: RendererProps): JSX.Element => {
    if (reportData.length > 0) {
      return (
        <>
          {reportData.map((refundData, index) => {
            return (
              <tr key={`${ColumeTitles.REFUNDS}_${index}`}>
                <td>{ColumeTitles.REFUNDS}</td>
                <td className='text-right'>{refundData[QueryMeasures.REFUNDS_COUNT]}</td>
                <td className='text-right'>
                  {GrPrice.convertDollarToDollar(refundData[QueryMeasures.REFUNDS_AMOUNT])}
                </td>
              </tr>
            )
          })}
        </>
      )
    } else {
      return <div>No data</div>
    }
  }

  renderActivationsChart(Component: React.FC<RendererProps>) {
    return this.renderChart(Component, 'dashboard:reports_giftCardsActivations')
  }

  renderRedemptionsChart(Component: React.FC<RendererProps>) {
    return this.renderChart(Component, 'dashboard:reports_giftCardsRedemptions')
  }

  renderDiscountChart(Component: React.FC<RendererProps>) {
    return this.renderChart(Component, 'dashboard:reports_giftCardsDiscounts')
  }

  renderRefundsChart(Component: React.FC<RendererProps>) {
    return this.renderChart(Component, 'dashboard:reports_giftCardsRefunds')
  }

  renderChart(Component: React.FC<RendererProps>, trackTitle: string) {
    const { startDate, endDate } = this.props.reportFilterStore!
    // eslint-disable-next-line react/prop-types
    return ({ resultSet, error }: { resultSet: ResultSet; error: any }) => {
      if (resultSet) {
        Track.track(trackTitle, {
          numberOfDays: moment(endDate).diff(startDate, 'days'),
        })
        this.data = this.data
          ? // eslint-disable-next-line react/prop-types
            [{ ...this.data[0], ...resultSet.rawData()[0] }]
          : // eslint-disable-next-line react/prop-types
            resultSet.rawData()
      } else {
        Track.startTimer(trackTitle)
        this.data = undefined
      }
      return (
        // eslint-disable-next-line react/prop-types
        (resultSet && <Component reportData={resultSet.rawData()} />) ||
        (error && error.toString()) || (
          <td colSpan={3}>
            <Skeleton active />
          </td>
        )
      )
    }
  }

  get formattedExportData() {
    const data = this.data

    if (!data) {
      return
    }

    return data.map((datum: any) => ({
      [CSVHeaders.ACTIVATIONS_COUNT]: datum[QueryMeasures.ACTIVATIONS_COUNT] || 0,
      [CSVHeaders.ACTIVATIONS_AMOUNT]: datum[QueryMeasures.ACTIVATIONS_AMOUNT] || 0,
      [CSVHeaders.REDEMPTIONS_COUNT]: datum[QueryMeasures.REDEMPTIONS_COUNT] || 0,
      [CSVHeaders.REDEMPTIONS_AMOUNT]: datum[QueryMeasures.REDEMPTIONS_AMOUNT] || 0,
      [CSVHeaders.DISCOUNTS_COUNT]: datum[QueryMeasures.DISCOUNTS_COUNT] || 0,
      [CSVHeaders.DISCOUNTS_AMOUNT]: datum[QueryMeasures.DISCOUNTS_AMOUNT] || 0,
      [CSVHeaders.REFUNDS_COUNT]: datum[QueryMeasures.REFUNDS_COUNT] || 0,
      [CSVHeaders.REFUNDS_AMOUNT]: datum[QueryMeasures.REFUNDS_AMOUNT] || 0,
    }))
  }

  exportCSV() {
    const { startDate, endDate } = this.props.reportFilterStore!

    if (this.formattedExportData) {
      parseAndDownloadCSV(`giftCards-${startDate}-${endDate}`, this.formattedExportData)
    }
  }

  getFullQuery(measures: string[], completeDate: string): ICubeQuery {
    const { startDateString, endDateString, selectedLocationIds } = this.props.reportFilterStore!

    return {
      measures,
      dimensions: [],
      limit,
      timeDimensions: [
        {
          dimension: `${completeDate}.completedate`,
          dateRange: [startDateString, endDateString],
        },
      ],
      filters: [
        {
          dimension: 'Payments.status',
          operator: 'equals',
          values: [`completed`],
        },
        {
          dimension: 'Locations.id',
          operator: 'equals',
          values: selectedLocationIds.map((id) => `${id}`),
        },
      ],
      order: {},
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
  }

  render() {
    const { companyId, reportFilterStore } = this.props
    const { startDateString, endDateString } = reportFilterStore!
    const { fullPaymentsQuery, fullTransactionsQuery, fullDiscountQuery, fullRefundsQuery } =
      this.state

    return (
      <>
        <Helmet>
          <title>Reports | Gift Cards</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ReportFilter />
        <div className='row'>
          <div className='col-md-6'>
            <div className='rounded background-white'>
              <div className='p-3'>
                <div className='d-flex justify-content-between'>
                  <b>Gift cards</b>
                  <Popover
                    content={
                      <Link
                        to={Routes.DASHBOARD_SETTINGS_GIFTCARDS(companyId)}
                        style={{ color: COLORS.black }}
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            textDecoration: 'underline',
                          }}
                        >
                          For comprehensive gift card data please visit your integrated gift card
                          provider.
                        </span>
                      </Link>
                    }
                    overlayStyle={{
                      width: '220px',
                    }}
                    placement='bottomRight'
                  >
                    <Button
                      shape='circle'
                      icon={
                        <QuestionCircleOutlined
                          style={{
                            fontSize: '19px',
                          }}
                        />
                      }
                      href={'#'}
                    />
                  </Popover>
                </div>

                <div className='mt-2 mb-2'>
                  <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
                    Export as csv
                  </button>
                </div>

                <div className='table-responsive'>
                  <table className='table table-sm'>
                    <thead>
                      <tr>
                        <th>Activity</th>
                        <th className='text-right'>Count</th>
                        <th className='text-right'>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fullPaymentsQuery && (
                        <QueryRenderer
                          query={fullPaymentsQuery}
                          cubejsApi={getCubeAPI(companyId)}
                          render={this.renderActivationsChart(this.tableRenderer)}
                        />
                      )}

                      {fullTransactionsQuery && (
                        <QueryRenderer
                          query={fullTransactionsQuery}
                          cubejsApi={getCubeAPI(companyId)}
                          render={this.renderRedemptionsChart(this.rowRenderer)}
                        />
                      )}

                      {fullDiscountQuery && (
                        <QueryRenderer
                          query={fullDiscountQuery}
                          cubejsApi={getCubeAPI(companyId)}
                          render={this.renderDiscountChart(this.discountRenderer)}
                        />
                      )}

                      {fullRefundsQuery && (
                        <QueryRenderer
                          query={fullRefundsQuery}
                          cubejsApi={getCubeAPI(companyId)}
                          render={this.renderRefundsChart(this.refundsRenderer)}
                        />
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }
}
